import { useState, useRef, useEffect, useCallback } from "react";
import {
  Sparkles, Download, Plus, X, Trash2, Loader2,
  Users, Settings2, Layers, Type, MessageSquare,
  Clapperboard, ArrowRight, Minus, BookOpen,
  PanelRightClose, PanelRightOpen, Clock, FolderOpen, FilePlus, List,
  Image, Grid3X3, Languages
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

const MODELS = [
  { provider: "openai", name: "gpt-5.2", label: "GPT 5.2" },
  { provider: "openai", name: "gpt-4.1", label: "GPT 4.1" },
  { provider: "anthropic", name: "claude-4-sonnet-20250514", label: "Claude 4 Sonnet" },
  { provider: "anthropic", name: "claude-sonnet-4-5-20250929", label: "Claude 4.5" },
  { provider: "gemini", name: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { provider: "gemini", name: "gemini-3-flash-preview", label: "Gemini 3 Flash" },
  { provider: "deepseek", name: "deepseek-chat", label: "DeepSeek V3" },
  { provider: "deepseek", name: "deepseek-r1", label: "DeepSeek R1" },
];

const GENRES = [
  "Action", "Adventure", "Animation", "Biography", "Comedy", "Crime",
  "Documentary", "Drama", "Family", "Fantasy", "Film Noir", "Historical",
  "Horror", "Musical", "Mystery", "Romance", "Sci-Fi", "Sport",
  "Superhero", "Thriller", "War", "Western", "Psychological", "Noir",
  "Satire", "Dystopian", "Epic", "Heist", "Survival", "Courtroom"
];

const LANGUAGES = [
  "English (US)", "English (UK)", "Turkish", "French", "Arabic", "Urdu", "Spanish"
];

const LINE_TYPES = [
  { id: "scene", label: "Scene Heading", shortcut: "1", icon: Clapperboard },
  { id: "action", label: "Action", shortcut: "2", icon: Type },
  { id: "character", label: "Character", shortcut: "3", icon: Users },
  { id: "dialogue", label: "Dialogue", shortcut: "4", icon: MessageSquare },
  { id: "parenthetical", label: "Parenthetical", shortcut: "5", icon: Minus },
  { id: "transition", label: "Transition", shortcut: "6", icon: ArrowRight },
];

// Smart duration: differentiates dialogue, action, and scene headings
const estimateDuration = (text) => {
  if (!text) return "0:00";
  const lines = text.split("\n");
  let totalSec = 0;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }
    // Scene heading (INT./EXT.) — establishment shot ~3-5 seconds
    if (/^(INT\.|EXT\.|INT\/EXT)/.test(line)) {
      totalSec += 4;
      i++;
      continue;
    }
    // Parenthetical — brief beat ~2 seconds
    if (line.startsWith("(") && line.endsWith(")")) {
      totalSec += 2;
      i++;
      continue;
    }
    // Character cue (ALL CAPS, short line before dialogue) — ~1 second
    const isUpperCase = line === line.toUpperCase() && line.length < 40 && /^[A-Z]/.test(line);
    if (isUpperCase) {
      totalSec += 1;
      i++;
      // Collect following dialogue lines until empty line or next cue
      let dialogueWords = 0;
      while (i < lines.length && lines[i].trim()) {
        const dl = lines[i].trim();
        if (dl.startsWith("(") && dl.endsWith(")")) { totalSec += 2; i++; continue; }
        if (dl === dl.toUpperCase() && dl.length < 40 && /^[A-Z]/.test(dl)) break;
        if (/^(INT\.|EXT\.|INT\/EXT)/.test(dl)) break;
        dialogueWords += dl.split(/\s+/).filter(Boolean).length;
        i++;
      }
      // Dialogue spoken at ~150 words per minute
      if (dialogueWords > 0) totalSec += Math.round((dialogueWords / 150) * 60);
      continue;
    }
    // Action/description — visual pacing ~180 words per minute
    const actionWords = line.split(/\s+/).filter(Boolean).length;
    totalSec += Math.round((actionWords / 180) * 60);
    i++;
  }
  // Minimum 1 second for any non-empty text
  if (totalSec === 0 && text.trim()) totalSec = 1;
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Extract scene headings from script text
const extractSceneHeadings = (text) => {
  if (!text) return [];
  return text.split("\n")
    .map(l => l.trim())
    .filter(l => l.startsWith("INT.") || l.startsWith("EXT.") || l.startsWith("INT/EXT"));
};

// Find scenes where a character appears
const findCharacterScenes = (charName, contextStack) => {
  if (!charName.trim()) return [];
  const upper = charName.trim().toUpperCase();
  const results = [];
  contextStack.forEach((txt, i) => {
    if (txt.toUpperCase().includes(upper)) {
      const headings = extractSceneHeadings(txt);
      results.push({ sceneIndex: i, headings: headings.length > 0 ? headings : ["(No heading)"] });
    }
  });
  return results;
};

// ======================== MAIN COMPONENT ========================
export const Scripting = () => {
  const [view, setView] = useState("dashboard"); // "dashboard" | "editor"
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Editor state
  const [workspaceId, setWorkspaceId] = useState(null);
  const [scriptTitle, setScriptTitle] = useState("Untitled Script");
  const [scriptAuthor, setScriptAuthor] = useState("");
  const [scriptContent, setScriptContent] = useState("");
  const [characters, setCharacters] = useState([{ name: "", description: "" }]);
  const [genre, setGenre] = useState("");
  const [language, setLanguage] = useState("English (US)");
  const [atmosphere, setAtmosphere] = useState("");
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [contextStack, setContextStack] = useState([]);
  const [leftPanel, setLeftPanel] = useState("ai");
  const [rightPanel, setRightPanel] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [sceneContext, setSceneContext] = useState("");
  const [sceneDirection, setSceneDirection] = useState("");
  const [expandedChar, setExpandedChar] = useState(null);
  const [storyboards, setStoryboards] = useState({}); // { sceneIndex: { status, panels, job_id } }
  const [storyboardProvider, setStoryboardProvider] = useState("openai"); // "openai" | "gemini"
  const [viewingStoryboard, setViewingStoryboard] = useState(null); // sceneIndex or null
  const [editingSceneIndex, setEditingSceneIndex] = useState(null); // which scene from contextStack is loaded in editor

  const progressRef = useRef(null);
  const saveTimerRef = useRef(null);
  const editorRef = useRef(null);

  // --- Load projects list ---
  useEffect(() => {
    if (view === "dashboard") {
      (async () => {
        setLoadingProjects(true);
        try {
          const res = await axios.get(`${API}/scripting/workspaces`, { timeout: 10000 });
          setProjects(res.data || []);
        } catch (_) {}
        setLoadingProjects(false);
      })();
    }
  }, [view]);

  // --- Load specific workspace ---
  const loadWorkspace = useCallback(async (id) => {
    setWorkspaceId(id);
    setLoaded(false);
    try {
      const res = await axios.get(`${API}/scripting/workspace/${id}`, { timeout: 10000 });
      if (res.data.exists) {
        const d = res.data;
        setScriptTitle(d.script_title || "Untitled Script");
        setScriptAuthor(d.script_author || "");
        setCharacters(d.characters?.length ? d.characters : [{ name: "", description: "" }]);
        setGenre(d.genre || "");
        setLanguage(d.language || "English (US)");
        setAtmosphere(d.atmosphere || "");
        setContextStack(d.context_stack || []);
        setScriptContent(d.generated_script || "");
        // Restore storyboards — mark saved panels as completed
        if (d.storyboards && typeof d.storyboards === "object") {
          const restored = {};
          Object.entries(d.storyboards).forEach(([idx, sb]) => {
            if (sb && sb.panels?.length > 0) {
              restored[idx] = { status: "completed", panels: sb.panels, completed: sb.panels.length, total: sb.panels.length };
            }
          });
          setStoryboards(restored);
        } else {
          setStoryboards({});
        }
        if (d.storyboard_provider) setStoryboardProvider(d.storyboard_provider);
        if (d.model_provider && d.model_name) {
          const m = MODELS.find(m => m.provider === d.model_provider && m.name === d.model_name);
          if (m) setSelectedModel(m);
        }
      } else {
        // New project — reset everything
        setScriptTitle("Untitled Script");
        setScriptAuthor("");
        setCharacters([{ name: "", description: "" }]);
        setGenre("");
        setLanguage("English (US)");
        setAtmosphere("");
        setContextStack([]);
        setScriptContent("");
        setSelectedModel(MODELS[0]);
        setStoryboards({});
      }
    } catch (_) {}
    setLoaded(true);
    setView("editor");
    setLeftPanel("ai");
  }, []);

  const createNewProject = () => {
    const id = crypto.randomUUID();
    loadWorkspace(id);
  };

  // --- Auto-save ---
  const saveWorkspace = useCallback(async (data) => {
    if (!data.workspaceId) return;
    setSaving(true);
    try {
      // Only save completed storyboards (strip transient status fields)
      const savedStoryboards = {};
      if (data.storyboards) {
        Object.entries(data.storyboards).forEach(([idx, sb]) => {
          if (sb && sb.status === "completed" && sb.panels?.length > 0) {
            savedStoryboards[idx] = { panels: sb.panels };
          }
        });
      }
      await axios.post(`${API}/scripting/workspace/save`, {
        workspace_id: data.workspaceId,
        script_title: data.scriptTitle,
        script_author: data.scriptAuthor,
        characters: data.characters,
        scenes: [{ context: data.sceneContext || "", direction: data.sceneDirection || "" }],
        genre: data.genre,
        language: data.language,
        atmosphere: data.atmosphere,
        model_provider: data.selectedModel.provider,
        model_name: data.selectedModel.name,
        context_stack: data.contextStack,
        generated_script: data.scriptContent,
        storyboards: savedStoryboards,
        storyboard_provider: data.storyboardProvider || "openai",
      }, { timeout: 15000 });
    } catch (_) {}
    setSaving(false);
  }, []);

  useEffect(() => {
    if (!loaded || !workspaceId) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveWorkspace({ workspaceId, scriptTitle, scriptAuthor, characters, genre, language, atmosphere, selectedModel, contextStack, scriptContent, sceneContext, sceneDirection, storyboards, storyboardProvider });
    }, 2000);
    return () => clearTimeout(saveTimerRef.current);
  }, [scriptTitle, scriptAuthor, characters, genre, language, atmosphere, selectedModel, contextStack, scriptContent, loaded, saveWorkspace, workspaceId, sceneContext, sceneDirection, storyboards, storyboardProvider]);

  // --- Handlers ---
  const addCharacter = () => setCharacters(prev => [...prev, { name: "", description: "" }]);
  const removeCharacter = (i) => setCharacters(prev => prev.filter((_, idx) => idx !== i));
  const updateCharacter = (i, f, v) => setCharacters(prev => prev.map((c, idx) => idx === i ? { ...c, [f]: v } : c));

  const insertLineType = (type) => {
    const el = editorRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const before = scriptContent.slice(0, start);
    const after = scriptContent.slice(el.selectionEnd);
    let insert = "";
    switch (type.id) {
      case "scene": insert = "\n\nINT. "; break;
      case "action": insert = "\n\n"; break;
      case "character": insert = "\n\n"; break;
      case "dialogue": insert = "\n    "; break;
      case "parenthetical": insert = "\n("; break;
      case "transition": insert = "\n\nCUT TO:\n"; break;
      default: insert = "\n";
    }
    const nc = before + insert + after;
    setScriptContent(nc);
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + insert.length; }, 0);
  };

  const handleGenerate = async () => {
    const validChars = characters.filter(c => c.name.trim());
    if (validChars.length === 0) return toast.error("Add at least one character");
    if (!sceneContext.trim()) return toast.error("Describe the scene to generate");
    setGenerating(true); setProgress(0);
    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      setProgress(Math.min(95, ((Date.now() - startTime) / 60000) * 100));
    }, 300);
    try {
      const startRes = await axios.post(`${API}/scripting/generate`, {
        characters: validChars, scenes: [{ context: sceneContext, direction: sceneDirection }],
        genre, language, atmosphere, model_provider: selectedModel.provider,
        model_name: selectedModel.name, combine_scenes: false,
        previous_context: contextStack.join("\n\n")
      }, { timeout: 15000 });
      const jobId = startRes.data.job_id;
      const result = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const r = await axios.get(`${API}/scripting/status/${jobId}`, { timeout: 10000 });
            if (r.data.status === "completed") { clearInterval(poll); resolve(r.data.script_text); }
            else if (r.data.status === "failed") { clearInterval(poll); reject(new Error(r.data.error || "Failed")); }
          } catch (_) {}
        }, 2000);
        setTimeout(() => { clearInterval(poll); reject(new Error("Timed out")); }, 180000);
      });
      setProgress(100);
      setScriptContent(prev => prev ? prev + "\n\n" + result : result);
      setContextStack(prev => [...prev, result]);
      setEditingSceneIndex(null);
      setSceneContext(""); setSceneDirection("");
      toast.success("Scene generated!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Generation failed");
    } finally {
      clearInterval(progressRef.current); setGenerating(false);
      setTimeout(() => setProgress(0), 500);
    }
  };

  const doExport = async (text, suffix = "") => {
    setExporting(true);
    try {
      const res = await axios.post(`${API}/scripting/export`, {
        script_text: text, title: scriptTitle, author: scriptAuthor
      }, { responseType: "blob", timeout: 30000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `${scriptTitle.replace(/\s+/g, "_")}${suffix}.docx`; a.click();
      window.URL.revokeObjectURL(url); toast.success("Exported!");
    } catch (_) { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const addManualContext = (text) => {
    if (!text.trim()) return;
    setContextStack(prev => [...prev, text.trim()]);
    toast.success("Added to context");
  };

  const deleteProject = async (id) => {
    try {
      await axios.delete(`${API}/scripting/workspace/${id}`, { timeout: 10000 });
      setProjects(prev => prev.filter(p => p.workspace_id !== id));
      toast.success("Project deleted");
    } catch (_) { toast.error("Delete failed"); }
  };

  // --- Open a scene from context stack in the editor ---
  const openSceneInEditor = (sceneIndex) => {
    setEditingSceneIndex(sceneIndex);
    setScriptContent(contextStack[sceneIndex]);
    toast.success(`Scene ${sceneIndex + 1} loaded in editor`);
  };

  // --- Translate the currently edited scene ---
  const [translating, setTranslating] = useState(false);
  const translateScene = async (targetLang) => {
    if (editingSceneIndex === null || !scriptContent.trim()) return;
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/scripting/translate`, {
        text: scriptContent,
        target_language: targetLang
      }, { timeout: 60000 });
      if (res.data.translated_text) {
        setScriptContent(res.data.translated_text);
        toast.success(`Translated to ${targetLang}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Translation failed");
    } finally { setTranslating(false); }
  };

  // --- Sync editor changes back to context stack when editing a scene ---
  useEffect(() => {
    if (editingSceneIndex === null || editingSceneIndex >= contextStack.length) return;
    if (scriptContent !== contextStack[editingSceneIndex]) {
      setContextStack(prev => prev.map((s, i) => i === editingSceneIndex ? scriptContent : s));
    }
  }, [scriptContent, editingSceneIndex]);

  const generateStoryboard = async (sceneIndex) => {
    const sceneText = contextStack[sceneIndex];
    if (!sceneText) return;
    setStoryboards(prev => ({ ...prev, [sceneIndex]: { status: "generating_prompts", panels: [], completed: 0, total: 1 } }));
    try {
      const res = await axios.post(`${API}/scripting/storyboard/generate`, {
        scene_text: sceneText, scene_index: sceneIndex,
        image_provider: storyboardProvider, panel_count: 1,
        characters: characters.filter(c => c.name.trim())
      }, { timeout: 15000 });
      const jobId = res.data.job_id;
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/scripting/storyboard/status/${jobId}`, { timeout: 10000 });
          setStoryboards(prev => ({
            ...prev, [sceneIndex]: {
              status: r.data.status, panels: r.data.panels || [],
              completed: r.data.completed_panels || 0, total: r.data.total_panels || 6
            }
          }));
          if (r.data.status === "completed" || r.data.status === "failed") {
            clearInterval(poll);
            if (r.data.status === "completed") toast.success(`Storyboard for Scene ${sceneIndex + 1} ready!`);
            else toast.error(r.data.error || "Storyboard generation failed");
          }
        } catch (_) {}
      }, 3000);
      setTimeout(() => clearInterval(poll), 600000); // 10 min max
    } catch (err) {
      toast.error("Failed to start storyboard generation");
      setStoryboards(prev => ({ ...prev, [sceneIndex]: { status: "failed", panels: [], completed: 0, total: 0 } }));
    }
  };

  const duration = estimateDuration(scriptContent);
  const totalCtxDuration = estimateDuration(contextStack.join("\n\n"));

  const sidebarItems = [
    { id: "ai", icon: Sparkles, label: "AI Generate" },
    { id: "chars", icon: Users, label: "Characters" },
    { id: "scenes", icon: List, label: "All Scenes" },
    { id: "context", icon: Layers, label: "Context" },
    { id: "settings", icon: Settings2, label: "Settings" },
  ];

  // ======================== DASHBOARD VIEW ========================
  if (view === "dashboard") {
    return (
      <div className="min-h-screen bg-[#1a1a2e] text-white" data-testid="projects-dashboard">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h1 className="font-['Syne'] text-3xl font-semibold tracking-tight">Script Projects</h1>
              <p className="text-white/30 text-sm mt-1">Select a project or create a new one</p>
            </div>
            <div className="flex gap-2">
              <a href="/" className="text-white/30 hover:text-white/60 text-xs px-3 py-2 border border-white/10 rounded transition-colors" data-testid="back-link">Back to Site</a>
              <button onClick={createNewProject}
                className="flex items-center gap-2 bg-[#5c5ce0] hover:bg-[#6b6be8] text-white text-sm px-4 py-2 rounded transition-colors"
                data-testid="new-project-btn">
                <FilePlus size={15} /> New Project
              </button>
            </div>
          </div>

          {loadingProjects ? (
            <div className="text-center py-20">
              <Loader2 size={24} className="animate-spin mx-auto text-white/30" />
              <p className="text-white/20 text-sm mt-3">Loading projects...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-white/10 rounded-lg">
              <FolderOpen size={40} className="mx-auto text-white/15 mb-4" />
              <p className="text-white/30 text-sm">No projects yet</p>
              <button onClick={createNewProject}
                className="mt-4 text-[#5c5ce0] hover:text-[#7c7cf0] text-sm transition-colors" data-testid="new-project-empty-btn">
                Create your first script
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map(p => (
                <div key={p.workspace_id}
                  className="group flex items-center gap-4 px-5 py-4 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.06] hover:border-white/[0.12] rounded-lg cursor-pointer transition-all"
                  onClick={() => loadWorkspace(p.workspace_id)}
                  data-testid={`project-${p.workspace_id}`}>
                  <div className="w-9 h-9 rounded bg-[#5c5ce0]/10 flex items-center justify-center shrink-0">
                    <Clapperboard size={16} className="text-[#5c5ce0]/60" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm text-white/80 font-medium truncate">{p.script_title || "Untitled Script"}</h3>
                    <p className="text-[10px] text-white/25 mt-0.5">
                      {p.scene_count || 0} scene{(p.scene_count || 0) !== 1 ? "s" : ""}
                      {p.updated_at && ` \u00B7 ${new Date(p.updated_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <button onClick={e => { e.stopPropagation(); deleteProject(p.workspace_id); }}
                    className="text-white/10 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-1"
                    data-testid={`delete-project-${p.workspace_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ======================== EDITOR VIEW ========================
  return (
    <div className="h-screen flex flex-col bg-[#1a1a2e] text-white overflow-hidden" data-testid="scripting-page">
      {/* TOP BAR */}
      <div className="h-10 bg-[#12122a] border-b border-white/[0.06] flex items-center px-3 gap-2 shrink-0">
        <button onClick={() => setView("dashboard")} className="text-white/30 hover:text-white/60 text-xs mr-1 transition-colors" data-testid="back-to-projects">
          <FolderOpen size={13} />
        </button>
        <div className="h-4 w-px bg-white/10" />
        <input value={scriptTitle} onChange={e => setScriptTitle(e.target.value)}
          className="bg-transparent text-white/80 text-sm font-medium px-2 py-0.5 hover:bg-white/5 focus:bg-white/5 focus:outline-none rounded w-48 truncate"
          data-testid="script-title" />
        <span className="text-white/20 text-xs">by</span>
        <input value={scriptAuthor} onChange={e => setScriptAuthor(e.target.value)} placeholder="Author"
          className="bg-transparent text-white/50 text-xs px-2 py-0.5 hover:bg-white/5 focus:bg-white/5 focus:outline-none rounded w-28 placeholder:text-white/20"
          data-testid="script-author" />
        <div className="flex-1" />
        <div className="flex items-center gap-1 text-[10px] text-white/25 mr-2"><Clock size={10} /><span data-testid="duration">{duration}</span></div>
        {saving && <span className="text-[10px] text-white/20 animate-pulse">Saving...</span>}
        <button onClick={() => doExport(scriptContent)} disabled={exporting || !scriptContent}
          className="text-xs text-white/40 hover:text-white/80 px-2 py-1 hover:bg-white/5 rounded transition-colors disabled:opacity-30" data-testid="export-btn" title="Export Scene"><Download size={13} /></button>
        {contextStack.length > 1 && (
          <button onClick={() => doExport(contextStack.join("\n\n"), "_full")} disabled={exporting}
            className="text-xs text-white/40 hover:text-white/80 px-2 py-1 hover:bg-white/5 rounded transition-colors disabled:opacity-30" data-testid="export-full-btn" title="Export Full Script"><BookOpen size={13} /></button>
        )}
      </div>

      {/* LINE TYPES BAR */}
      <div className="h-8 bg-[#16162e] border-b border-white/[0.06] flex items-center px-3 gap-0.5 shrink-0">
        {LINE_TYPES.map(lt => (
          <button key={lt.id} onClick={() => insertLineType(lt)}
            className="text-[10px] text-white/40 hover:text-white/80 hover:bg-white/5 px-2.5 py-1 rounded transition-colors flex items-center gap-1.5"
            title={`${lt.label} (${lt.shortcut})`} data-testid={`lt-${lt.id}`}>
            <lt.icon size={11} /><span className="hidden sm:inline">{lt.label}</span>
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={() => setRightPanel(!rightPanel)}
          className="text-white/30 hover:text-white/60 p-1 rounded transition-colors" data-testid="toggle-right-panel">
          {rightPanel ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
        </button>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar Icons */}
        <div className="w-11 bg-[#12122a] border-r border-white/[0.06] flex flex-col items-center pt-2 gap-1 shrink-0">
          {sidebarItems.map(item => (
            <button key={item.id} onClick={() => setLeftPanel(leftPanel === item.id ? null : item.id)}
              className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${leftPanel === item.id ? "bg-white/10 text-white/80" : "text-white/30 hover:text-white/60 hover:bg-white/5"}`}
              title={item.label} data-testid={`sidebar-${item.id}`}>
              <item.icon size={15} />
            </button>
          ))}
        </div>

        {/* Left Panel */}
        {leftPanel && (
          <div className="w-64 bg-[#14142c] border-r border-white/[0.06] overflow-y-auto shrink-0" data-testid="left-panel">
            <div className="p-3">
              {/* AI Panel */}
              {leftPanel === "ai" && (
                <div className="space-y-3" data-testid="ai-panel">
                  <h3 className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">AI Generate</h3>
                  <select value={`${selectedModel.provider}/${selectedModel.name}`}
                    onChange={e => { const [p, n] = e.target.value.split("/"); setSelectedModel(MODELS.find(m => m.provider === p && m.name === n) || MODELS[0]); }}
                    className="w-full bg-white/5 border border-white/10 text-white/70 text-xs px-2 py-1.5 rounded focus:outline-none"
                    data-testid="model-select" style={{ colorScheme: "dark" }}>
                    {MODELS.map(m => <option key={`${m.provider}/${m.name}`} value={`${m.provider}/${m.name}`} style={{ color: "#000", background: "#fff" }}>{m.label}</option>)}
                  </select>
                  <textarea value={sceneContext} onChange={e => setSceneContext(e.target.value)}
                    placeholder="Describe what happens in this scene..."
                    className="w-full bg-white/5 border border-white/10 text-white/80 text-xs px-2 py-2 rounded focus:outline-none focus:border-white/20 placeholder:text-white/20 resize-none"
                    rows={4} data-testid="scene-context-0" />
                  <textarea value={sceneDirection} onChange={e => setSceneDirection(e.target.value)}
                    placeholder="Direction notes (mood, pacing)..."
                    className="w-full bg-white/5 border border-white/10 text-white/80 text-xs px-2 py-2 rounded focus:outline-none focus:border-white/20 placeholder:text-white/20 resize-none"
                    rows={2} data-testid="scene-direction-0" />
                  <button onClick={handleGenerate} disabled={generating}
                    className="w-full py-2 bg-[#5c5ce0] hover:bg-[#6b6be8] text-white text-xs font-medium rounded transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                    data-testid="generate-btn">
                    {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {generating ? "Generating..." : "Generate Scene"}
                  </button>
                  {generating && (
                    <div data-testid="progress-bar">
                      <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-[#5c5ce0] rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-[9px] text-white/20 mt-0.5 block">{Math.round(progress)}%</span>
                    </div>
                  )}
                </div>
              )}

              {/* Characters Panel with Scene Tracker */}
              {leftPanel === "chars" && (
                <div className="space-y-3" data-testid="chars-panel">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">Characters</h3>
                    <button onClick={addCharacter} className="text-white/30 hover:text-white/60" data-testid="add-character-btn"><Plus size={13} /></button>
                  </div>
                  {characters.map((c, i) => {
                    const charScenes = c.name.trim() ? findCharacterScenes(c.name, contextStack) : [];
                    const isExpanded = expandedChar === i;
                    return (
                      <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded p-2 space-y-1.5">
                        <div className="flex gap-1.5">
                          <input value={c.name} onChange={e => updateCharacter(i, "name", e.target.value)}
                            placeholder="Name" className="flex-1 bg-transparent border border-white/10 text-white/80 text-xs px-2 py-1 rounded focus:outline-none placeholder:text-white/20"
                            data-testid={`char-name-${i}`} />
                          {characters.length > 1 && (
                            <button onClick={() => removeCharacter(i)} className="text-white/15 hover:text-red-400 px-1"><X size={11} /></button>
                          )}
                        </div>
                        <textarea value={c.description} onChange={e => updateCharacter(i, "description", e.target.value)}
                          placeholder="Description..." rows={2}
                          className="w-full bg-transparent border border-white/10 text-white/60 text-[11px] px-2 py-1 rounded focus:outline-none placeholder:text-white/15 resize-none"
                          data-testid={`char-desc-${i}`} />
                        {/* Scene tracker */}
                        {c.name.trim() && (
                          <button onClick={() => setExpandedChar(isExpanded ? null : i)}
                            className="text-[9px] text-[#5c5ce0]/60 hover:text-[#5c5ce0] transition-colors flex items-center gap-1"
                            data-testid={`char-scenes-toggle-${i}`}>
                            <Clapperboard size={9} />
                            {charScenes.length} scene{charScenes.length !== 1 ? "s" : ""}
                          </button>
                        )}
                        {isExpanded && charScenes.length > 0 && (
                          <div className="bg-white/[0.02] border border-white/[0.04] rounded p-1.5 space-y-1" data-testid={`char-scenes-list-${i}`}>
                            {charScenes.map((s, si) => (
                              <div key={si}>
                                {s.headings.map((h, hi) => (
                                  <p key={hi} className="text-[9px] text-white/30 font-mono truncate" title={h}>
                                    Scene {s.sceneIndex + 1}: {h}
                                  </p>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* All Scenes Tab */}
              {leftPanel === "scenes" && (
                <div className="space-y-3" data-testid="scenes-panel">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">All Scenes ({contextStack.length})</h3>
                    {contextStack.length > 0 && <span className="text-[9px] text-white/20 font-mono">{totalCtxDuration}</span>}
                  </div>
                  {/* Storyboard image provider selector */}
                  {contextStack.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] text-white/20">Storyboard:</span>
                      <button onClick={() => setStoryboardProvider("openai")}
                        className={`text-[9px] px-1.5 py-0.5 rounded transition-colors ${storyboardProvider === "openai" ? "bg-[#5c5ce0]/30 text-[#9c9cf0]" : "text-white/25 hover:text-white/50"}`}
                        data-testid="sb-provider-openai">GPT</button>
                      <button onClick={() => setStoryboardProvider("gemini")}
                        className={`text-[9px] px-1.5 py-0.5 rounded transition-colors ${storyboardProvider === "gemini" ? "bg-[#5c5ce0]/30 text-[#9c9cf0]" : "text-white/25 hover:text-white/50"}`}
                        data-testid="sb-provider-gemini">Gemini</button>
                    </div>
                  )}
                  {contextStack.length === 0 ? (
                    <p className="text-[10px] text-white/15 italic">No scenes generated yet.</p>
                  ) : (
                    contextStack.map((txt, i) => {
                      const headings = extractSceneHeadings(txt);
                      const dur = estimateDuration(txt);
                      const words = txt.trim().split(/\s+/).filter(Boolean).length;
                      const charsInScene = characters.filter(c => c.name.trim() && txt.toUpperCase().includes(c.name.trim().toUpperCase()));
                      const sb = storyboards[i];
                      const sbGenerating = sb && (sb.status === "generating_prompts" || sb.status === "generating_images");
                      return (
                        <div key={i} className={`bg-white/[0.03] border rounded p-2.5 group cursor-pointer transition-all ${editingSceneIndex === i ? "border-[#5c5ce0]/50 bg-[#5c5ce0]/5" : "border-white/[0.06] hover:border-white/[0.12]"}`}
                          onDoubleClick={() => openSceneInEditor(i)}
                          data-testid={`scene-card-${i}`}
                          title="Double-click to edit in canvas">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] text-white/40 font-semibold">Scene {i + 1}</span>
                            <span className="text-[9px] text-white/20 font-mono">{dur}</span>
                          </div>
                          {headings.length > 0 ? (
                            headings.map((h, hi) => (
                              <p key={hi} className="text-[10px] text-white/50 font-mono font-bold truncate" title={h}>{h}</p>
                            ))
                          ) : (
                            <p className="text-[10px] text-white/25 italic">No scene heading</p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[8px] text-white/15">{words} words</span>
                            {charsInScene.length > 0 && (
                              <span className="text-[8px] text-[#5c5ce0]/40">{charsInScene.map(c => c.name).join(", ")}</span>
                            )}
                          </div>
                          {/* Storyboard section */}
                          <div className="mt-2 pt-2 border-t border-white/[0.04]">
                            {!sb || sb.status === "failed" ? (
                              <button onClick={() => generateStoryboard(i)} disabled={sbGenerating}
                                className="w-full text-[9px] text-white/30 hover:text-[#5c5ce0] hover:bg-[#5c5ce0]/5 border border-dashed border-white/10 hover:border-[#5c5ce0]/30 py-1.5 rounded flex items-center justify-center gap-1 transition-colors"
                                data-testid={`storyboard-btn-${i}`}>
                                <Grid3X3 size={10} /> Generate Storyboard
                              </button>
                            ) : sbGenerating ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5 text-[9px] text-white/30">
                                  <Loader2 size={10} className="animate-spin" />
                                  <span>{sb.status === "generating_prompts" ? "Analyzing scene..." : "Generating storyboard..."}</span>
                                </div>
                                <div className="w-full h-0.5 bg-white/5 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#5c5ce0] rounded-full transition-all duration-500" style={{ width: `${sb.total > 0 ? (sb.completed / sb.total) * 100 : 10}%` }} />
                                </div>
                                {/* Show panels as they come in */}
                                {sb.panels.length > 0 && (
                                  <button onClick={() => setViewingStoryboard(i)} className="text-[8px] text-[#5c5ce0]/50 hover:text-[#5c5ce0]">
                                    Preview storyboard
                                  </button>
                                )}
                              </div>
                            ) : sb.status === "completed" ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => setViewingStoryboard(viewingStoryboard === i ? null : i)}
                                  className="flex-1 text-[9px] text-[#5c5ce0]/60 hover:text-[#5c5ce0] bg-[#5c5ce0]/5 border border-[#5c5ce0]/10 py-1.5 rounded flex items-center justify-center gap-1 transition-colors"
                                  data-testid={`view-storyboard-${i}`}>
                                  <Grid3X3 size={10} /> View Storyboard
                                </button>
                                <button onClick={() => { setStoryboards(prev => { const n = { ...prev }; delete n[i]; return n; }); toast.success("Storyboard deleted"); }}
                                  className="text-[9px] text-white/20 hover:text-red-400 border border-white/[0.06] hover:border-red-400/30 p-1.5 rounded transition-colors"
                                  data-testid={`delete-storyboard-${i}`} title="Delete storyboard">
                                  <Trash2 size={10} />
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Context Panel */}
              {leftPanel === "context" && (
                <ContextPanel contextStack={contextStack} setContextStack={setContextStack}
                  addManualContext={addManualContext} totalCtxDuration={totalCtxDuration} doExport={doExport} exporting={exporting} />
              )}

              {/* Settings Panel */}
              {leftPanel === "settings" && (
                <div className="space-y-3" data-testid="settings-panel">
                  <h3 className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">Script Settings</h3>
                  <div><label className="text-[10px] text-white/25 block mb-1">Genre</label>
                    <select value={genre} onChange={e => setGenre(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 text-white/70 text-xs px-2 py-1.5 rounded focus:outline-none"
                      data-testid="genre-select" style={{ colorScheme: "dark" }}>
                      <option value="" style={{ color: "#000", background: "#fff" }}>Select</option>{GENRES.map(g => <option key={g} value={g} style={{ color: "#000", background: "#fff" }}>{g}</option>)}
                    </select></div>
                  <div><label className="text-[10px] text-white/25 block mb-1">Language</label>
                    <select value={language} onChange={e => setLanguage(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 text-white/70 text-xs px-2 py-1.5 rounded focus:outline-none"
                      data-testid="language-select" style={{ colorScheme: "dark" }}>
                      {LANGUAGES.map(l => <option key={l} value={l} style={{ color: "#000", background: "#fff" }}>{l}</option>)}
                    </select></div>
                  <div><label className="text-[10px] text-white/25 block mb-1">Atmosphere</label>
                    <input value={atmosphere} onChange={e => setAtmosphere(e.target.value)} placeholder="e.g., Tense, moody..."
                      className="w-full bg-white/5 border border-white/10 text-white/70 text-xs px-2 py-1.5 rounded focus:outline-none placeholder:text-white/15"
                      data-testid="atmosphere-input" /></div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CENTRAL EDITOR */}
        <div className="flex-1 min-w-0 bg-[#1e1e3a] overflow-auto flex justify-center" data-testid="editor-container">
          <div className="w-full max-w-[680px] my-8 mx-4">
            {editingSceneIndex !== null && (
              <div className="flex items-center justify-between bg-[#5c5ce0]/10 border border-[#5c5ce0]/20 rounded-t px-4 py-1.5 mb-0">
                <span className="text-[11px] text-[#9c9cf0] font-medium">Editing Scene {editingSceneIndex + 1}</span>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Languages size={11} className="text-white/40" />
                    <select
                      onChange={e => { if (e.target.value) translateScene(e.target.value); e.target.value = ""; }}
                      disabled={translating}
                      className="bg-transparent text-[10px] text-white/50 hover:text-white/80 focus:outline-none cursor-pointer"
                      style={{ colorScheme: "dark" }}
                      data-testid="translate-select">
                      <option value="" style={{ color: "#000" }}>{translating ? "Translating..." : "Translate to..."}</option>
                      {LANGUAGES.map(l => <option key={l} value={l} style={{ color: "#000" }}>{l}</option>)}
                    </select>
                  </div>
                  <div className="h-3 w-px bg-white/10" />
                  <button onClick={() => { setEditingSceneIndex(null); setScriptContent(contextStack.join("\n\n")); }}
                    className="text-[10px] text-white/40 hover:text-white/70 transition-colors" data-testid="exit-scene-edit">
                    Show All
                  </button>
                </div>
              </div>
            )}
            <div className="bg-[#faf9f6] shadow-2xl shadow-black/40 min-h-[calc(100vh-200px)]">
              <textarea ref={editorRef} value={scriptContent}
                onChange={e => setScriptContent(e.target.value)}
                placeholder={"Start writing your screenplay...\n\nINT. LOCATION - TIME\n\nAction description goes here.\n\nCHARACTER NAME\nDialogue goes here."}
                className="w-full min-h-[calc(100vh-200px)] bg-transparent text-[#1a1a1a] font-['Courier_Prime','Courier_New',monospace] text-[13px] leading-[1.6] px-[72px] py-[60px] resize-none focus:outline-none placeholder:text-[#ccc] placeholder:leading-[1.6]"
                spellCheck={true} data-testid="script-editor" />
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        {rightPanel && (
          <div className="w-60 bg-[#14142c] border-l border-white/[0.06] overflow-y-auto shrink-0" data-testid="right-panel">
            <div className="p-3">
              <ContextPanel contextStack={contextStack} setContextStack={setContextStack}
                addManualContext={addManualContext} totalCtxDuration={totalCtxDuration} doExport={doExport} exporting={exporting} />
            </div>
          </div>
        )}
      </div>

      {/* STORYBOARD OVERLAY */}
      {viewingStoryboard !== null && storyboards[viewingStoryboard] && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setViewingStoryboard(null)} data-testid="storyboard-overlay">
          <div className="bg-[#14142c] border border-white/10 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-white/80 font-['Syne'] text-lg font-semibold">Storyboard &mdash; Scene {viewingStoryboard + 1}</h2>
                <p className="text-[10px] text-white/25 mt-0.5">
                  {storyboardProvider === "gemini" ? "Gemini Nano Banana" : "GPT Image 1"}
                </p>
              </div>
              <button onClick={() => setViewingStoryboard(null)} className="text-white/30 hover:text-white/70 transition-colors" data-testid="close-storyboard"><X size={18} /></button>
            </div>
            {storyboards[viewingStoryboard].panels.map((panel, pi) => (
              <div key={pi} className="bg-white/[0.03] border border-white/[0.06] rounded overflow-hidden" data-testid={`storyboard-panel-${pi}`}>
                <div className="bg-[#0a0a1a] flex items-center justify-center">
                  {panel.image_b64 ? (
                    <img src={`data:image/png;base64,${panel.image_b64}`} alt={panel.caption} className="w-full h-auto object-contain" />
                  ) : (
                    <div className="text-white/10 text-xs py-20">Image generation failed</div>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-[11px] text-white/50 font-medium">{panel.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BOTTOM BAR */}
      <div className="h-6 bg-[#12122a] border-t border-white/[0.06] flex items-center px-3 shrink-0">
        <span className="text-[9px] text-white/20 mr-3">{scriptContent.split(/\s+/).filter(Boolean).length} words</span>
        <span className="text-[9px] text-white/20 mr-3">{scriptContent.split("\n").length} lines</span>
        <span className="text-[9px] text-white/20">{contextStack.length} scene{contextStack.length !== 1 ? "s" : ""}</span>
        <div className="flex-1" />
        <span className="text-[9px] text-white/15">{selectedModel.label}</span>
      </div>
    </div>
  );
};

// ======================== CONTEXT PANEL ========================
const ContextPanel = ({ contextStack, setContextStack, addManualContext, totalCtxDuration, doExport, exporting }) => {
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState("");
  return (
    <div className="space-y-3" data-testid="context-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] uppercase tracking-widest text-white/30 font-semibold">Context ({contextStack.length})</h3>
        {contextStack.length > 0 && <span className="text-[9px] text-white/20 font-mono">{totalCtxDuration}</span>}
      </div>
      {contextStack.length === 0 && !manualOpen && <p className="text-[10px] text-white/15 italic">Scenes stack here as you generate.</p>}
      {contextStack.map((txt, i) => (
        <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded p-2 group relative">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[9px] text-white/25 font-mono">Scene {i + 1} &middot; {estimateDuration(txt)}</span>
            <button onClick={() => setContextStack(prev => prev.filter((_, idx) => idx !== i))}
              className="text-white/10 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`context-remove-${i}`}><X size={10} /></button>
          </div>
          <p className="text-[10px] text-white/35 leading-relaxed line-clamp-3">{txt.slice(0, 150)}...</p>
        </div>
      ))}
      {manualOpen ? (
        <div className="space-y-1.5" data-testid="manual-context-form">
          <textarea value={manualText} onChange={e => setManualText(e.target.value)} placeholder="Paste scene text or notes..."
            className="w-full bg-white/5 border border-white/10 text-white/70 text-[11px] px-2 py-1.5 rounded focus:outline-none placeholder:text-white/15 resize-none"
            rows={4} data-testid="manual-context-input" />
          <div className="flex gap-1.5">
            <button onClick={() => { addManualContext(manualText); setManualText(""); setManualOpen(false); }}
              className="text-[10px] bg-white/10 hover:bg-white/15 text-white/60 px-2 py-1 rounded" data-testid="manual-context-add-btn">Add</button>
            <button onClick={() => { setManualOpen(false); setManualText(""); }}
              className="text-[10px] text-white/25 hover:text-white/40 px-2 py-1">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setManualOpen(true)}
          className="w-full border border-dashed border-white/10 hover:border-white/20 text-white/25 hover:text-white/45 text-[10px] py-2 rounded flex items-center justify-center gap-1 transition-colors"
          data-testid="manual-context-open-btn"><Plus size={10} /> Add Manually</button>
      )}
      {contextStack.length > 0 && (
        <div className="flex gap-2 pt-1 border-t border-white/[0.04]">
          <button onClick={() => setContextStack([])} className="text-[9px] text-white/20 hover:text-red-400" data-testid="context-clear">Clear</button>
          <button onClick={() => doExport(contextStack.join("\n\n"), "_full")} disabled={exporting}
            className="text-[9px] text-white/20 hover:text-white/50 ml-auto flex items-center gap-1" data-testid="export-full-ctx-btn"><Download size={8} /> Full Script</button>
        </div>
      )}
    </div>
  );
};
